"use client";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";

import ConfirmDialog from "@/components/ConfirmDialog";
import type { SiblingStore } from "@/server-fns/booking/query";
import { formatStoreAddress } from "@/utils/emails/bookingEmailContent";

type StoreAddress = {
    streetAddress: string;
    suburb: string;
    state: string;
    postcode: string;
};

type Props = {
    open: boolean;
    storeName: string;
    storeAddress: StoreAddress;
    siblingStores: SiblingStore[];
    onConfirm: () => void;
    onClose: () => void;
};

/**
 * Pre-submit "is this the right location?" check, shown once per store (see BookingSummary's
 * localStorage dismiss key) right before the booking is sent — only when the owner has other
 * active stores, so a guest who followed the wrong link catches it before submitting.
 */
export const StoreConfirmDialog = ({
    open,
    storeName,
    storeAddress,
    siblingStores,
    onConfirm,
    onClose,
}: Props) => {
    const [showOtherLocations, setShowOtherLocations] = useState(false);

    return (
        <ConfirmDialog
            open={open}
            onClose={() => {
                setShowOtherLocations(false);
                onClose();
            }}
            title="Is this the right location?"
            showPrimaryButton={false}
            showSecondaryButton={false}
            showWarningIcon={false}
        >
            <Stack spacing={2}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {storeName}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 0.5 }}
                    >
                        <LocationOnIcon sx={{ fontSize: 18, mt: "2px" }} />
                        {formatStoreAddress(storeAddress)}
                    </Typography>
                </Box>

                {!showOtherLocations ? (
                    <Stack spacing={1}>
                        <Button variant="contained" fullWidth onClick={onConfirm}>
                            Yes, this is my location
                        </Button>
                        <Button
                            variant="text"
                            fullWidth
                            onClick={() => setShowOtherLocations(true)}
                        >
                            Find another location
                        </Button>
                    </Stack>
                ) : (
                    <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                            {storeName} has {siblingStores.length} other location
                            {siblingStores.length === 1 ? "" : "s"}:
                        </Typography>
                        {siblingStores.map((sibling) => (
                            <Button
                                key={sibling.id}
                                variant="outlined"
                                fullWidth
                                href={`/${sibling.id}/${sibling.slug}`}
                                sx={{ justifyContent: "flex-start", textAlign: "left" }}
                            >
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {sibling.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatStoreAddress(sibling)}
                                    </Typography>
                                </Box>
                            </Button>
                        ))}
                        <Button
                            variant="text"
                            fullWidth
                            onClick={() => setShowOtherLocations(false)}
                        >
                            Back
                        </Button>
                    </Stack>
                )}
            </Stack>
        </ConfirmDialog>
    );
};
